import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useAvailableBrandsQuery, useAvailableColorsQuery, useAvailableStylesQuery } from '@/queries/my-style'
import {
  isPersonTuned,
  resolvePersonTaste,
  type PersonTaste,
  type ResolvedPersonTaste,
} from './person-taste.data'
import { usePersonTasteStore } from './store/usePersonTasteStore'

/**
 * Turns taste ids into Taste preview items (style photos, color hex, brand logos) from
 * the same catalogues the quiz picks from. Call once per screen and pass `resolve` down.
 */
export function usePersonTasteResolver(
  /** T-168 — the person's style set (man / woman); default — the shopper's own catalogue. */
  audience?: MaybeRefOrGetter<'woman' | 'man' | null | undefined>,
) {
  const stylesQuery = useAvailableStylesQuery(audience)
  const colorsQuery = useAvailableColorsQuery()
  const brandsQuery = useAvailableBrandsQuery()

  const catalog = computed(() => ({
    styles: stylesQuery.data.value ?? [],
    colors: colorsQuery.data.value ?? [],
    brands: brandsQuery.data.value ?? [],
  }))

  /** True until all three catalogues have answered once (cards show skeleton rows). */
  const isLoading = computed(
    () =>
      (!stylesQuery.data.value && stylesQuery.isLoading.value) ||
      (!colorsQuery.data.value && colorsQuery.isLoading.value) ||
      (!brandsQuery.data.value && brandsQuery.isLoading.value),
  )

  function resolve(taste: PersonTaste | null | undefined): ResolvedPersonTaste {
    return resolvePersonTaste(taste, catalog.value)
  }

  return { resolve, isLoading, catalog }
}

/** One person's taste, tuned flag and setters — reactive to the quiz and other tabs. */
export function usePersonTaste(personId: MaybeRefOrGetter<number | null | undefined>) {
  const store = usePersonTasteStore()
  const taste = computed(() => store.get(toValue(personId)))
  const tuned = computed(() => isPersonTuned(taste.value))
  return {
    taste,
    tuned,
    patch: (patch: Partial<PersonTaste>) => store.patchTaste(toValue(personId), patch),
    setStyles: (ids: number[]) => store.setStyles(toValue(personId), ids),
    setColors: (ids: number[]) => store.setColors(toValue(personId), ids),
    setBrands: (ids: number[]) => store.setBrands(toValue(personId), ids),
  }
}

export type { ResolvedPersonTaste }
